import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../components/AuthContext';
import api from '../../api';
import Swal from 'sweetalert2';
import Select from 'react-select';
import QRCode from 'react-qr-code';
import jsPDF from 'jspdf';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import QRCodeLib from 'qrcode';
import { 
  FaSearch, 
  FaEdit, 
  FaToggleOn, 
  FaToggleOff, 
  FaBox, 
  FaExclamationTriangle, 
  FaSave,
  FaTimes,
  FaWarehouse,
  FaSync,
  FaQrcode,
  FaFilePdf,
  FaFileImage,
  FaTh,
  FaList,
  FaFileExport,
  FaCheckSquare,
  FaSquare,
  FaFileExcel,
  FaCubes,
  FaTruck,
  FaCalendarAlt
} from 'react-icons/fa';

const StockItemsManagement = () => {
  const { user } = useContext(AuthContext);
  
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');
  
  useEffect(() => {
    const interval = setInterval(() => {
      setDarkMode(localStorage.getItem('darkMode') === 'true');
    }, 500);
    return () => clearInterval(interval);
  }, []);
  
  const [stockItems, setStockItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showStockModal, setShowStockModal] = useState(false);
  const [statistics, setStatistics] = useState({});
  const [locations, setLocations] = useState([]);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  
  // Filters
  const [filters, setFilters] = useState({
    category: null,
    brand: null,
    status: null
  });

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const itemsPerPage = 20;

  // Stock editing
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [stockValues, setStockValues] = useState({
    freshQuantity: 0,
    damagedQuantity: 0
  });

  // QR Code modal states
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrItem, setQrItem] = useState(null);

  // View mode and selection states
  const [viewMode, setViewMode] = useState('table');
  const [selectedItems, setSelectedItems] = useState([]);
  const [showBulkQRModal, setShowBulkQRModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [bulkQRFilters, setBulkQRFilters] = useState({
    brand: null,
    category: null,
    status: 'active'
  });
  const [bulkQRItems, setBulkQRItems] = useState([]);
  const [loadingBulkQR, setLoadingBulkQR] = useState(false);

  // Export Stock modal states
  const [showExportStockModal, setShowExportStockModal] = useState(false);
  const [exportStockFilters, setExportStockFilters] = useState({
    brand: null,
    category: null,
    status: 'active',
    location: null
  });

  // Stock Movement Export modal states
  const [showStockMovementModal, setShowStockMovementModal] = useState(false);
  const [stockMovementLoading, setStockMovementLoading] = useState(false);
  const [stockMovementFilters, setStockMovementFilters] = useState(() => {
    const now = new Date();
    const fyYear = now.getMonth() < 3 ? now.getFullYear() - 1 : now.getFullYear();
    return {
      startDate: `${fyYear}-04-01`,
      endDate: now.toISOString().split('T')[0],
      location: null,
      brand: null,
      category: null
    };
  });

  // Edit mode states
  const [isEditingItem, setIsEditingItem] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: '',
    partNumber: '',
    brand: '',
    category: '',
    unitOfMeasure: '',
    status: 'active'
  });

  const darkSelectStyles = darkMode ? {
    control: (base) => ({ ...base, backgroundColor: '#374151', borderColor: '#4B5563', color: '#fff' }),
    singleValue: (base) => ({ ...base, color: '#fff' }),
    menu: (base) => ({ ...base, backgroundColor: '#374151' }),
    option: (base, state) => ({ ...base, backgroundColor: state.isFocused ? '#4B5563' : '#374151', color: '#fff' }),
    input: (base) => ({ ...base, color: '#fff' }),
    placeholder: (base) => ({ ...base, color: '#9CA3AF' })
  } : {};

  const getCompanyId = () => {
    const company = user?.user?.company;
    if (!company) return null;
    if (typeof company === 'string') return company;
    return company._id || company.id || null;
  };

  useEffect(() => {
    const companyId = getCompanyId();
    if (!companyId) return;
    fetchStockItems();
    fetchStatistics();
    fetchFilterOptions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchTerm, filters, user]);

  const fetchStockItems = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        companyId: getCompanyId(),
        page: currentPage,
        limit: itemsPerPage,
        ...(searchTerm && { search: searchTerm }),
        ...(filters.category && { category: filters.category }),
        ...(filters.brand && { brand: filters.brand }),
        ...(filters.status && { status: filters.status })
      });

      const response = await api.get(`/stock-items/management/all?${params}`);
      
      if (response.data.success) {
        setStockItems(response.data.data.stockItems);
        setTotalPages(response.data.data.pagination.total);
        setTotalRecords(response.data.data.pagination.totalRecords);
      }
    } catch (error) {
      console.error('Error fetching stock items:', error);
      Swal.fire('Error', 'Failed to fetch stock items', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await api.get(`/stock-items/management/statistics/${getCompanyId()}`);
      if (response.data.success) {
        setStatistics(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const [locationsRes, brandsRes, categoriesRes] = await Promise.all([
        api.get(`/locations/companyId/${getCompanyId()}`),
        api.get('/brands'),
        api.get('/categories')
      ]);

      const locationsData = locationsRes.data || [];
      const brandsData = brandsRes.data?.data || brandsRes.data || [];
      const categoriesData = categoriesRes.data?.data || categoriesRes.data || [];
      
      setLocations(locationsData);
      setBrands(brandsData);
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error fetching filter options:', error);
      setCategories([]);
    }
  };

  const handleStockEdit = (item, location) => {
    setSelectedItem(item);
    setSelectedLocation(location);
    
    const existingStock = item.stock?.find(s => s.location?._id === location._id || s.location === location._id);
    if (existingStock) {
      setStockValues({
        freshQuantity: existingStock.freshQuantity || 0,
        damagedQuantity: existingStock.damagedQuantity || 0
      });
    } else {
      setStockValues({ freshQuantity: 0, damagedQuantity: 0 });
    }
    
    setShowStockModal(true);
  };

  const handleStockUpdate = async () => {
    try {
      const response = await api.put(
        `/stock-items/edit-stock/${selectedItem._id}/${selectedLocation._id}`,
        stockValues
      );

      if (response.data.success) {
        Swal.fire('Success', 'Stock updated successfully', 'success');
        setShowStockModal(false);
        fetchStockItems();
        fetchStatistics();
      }
    } catch (error) {
      console.error('Error updating stock:', error);
      Swal.fire('Error', error.response?.data?.message || 'Failed to update stock', 'error');
    }
  };

  const handleEditItem = () => {
    setEditFormData({
      name: selectedItem.name || '',
      partNumber: selectedItem.partNumber || '',
      brand: selectedItem.brand?._id || '',
      category: selectedItem.category?._id || '',
      unitOfMeasure: selectedItem.unitOfMeasure || '',
      status: selectedItem.status || 'active'
    });
    setIsEditingItem(true);
  };

  const handleCancelEdit = () => {
    setIsEditingItem(false);
    setEditFormData({ name: '', partNumber: '', brand: '', category: '', unitOfMeasure: '', status: 'active' });
  };

  const handleSaveItem = async () => {
    try {
      const response = await api.put(
        `/stock-items/management/${selectedItem._id}`,
        editFormData
      );

      if (response.data.success) {
        Swal.fire('Success', 'Stock item updated successfully', 'success');
        setIsEditingItem(false);
        setSelectedItem(response.data.data);
        fetchStockItems();
        fetchStatistics();
      }
    } catch (error) {
      console.error('Error updating stock item:', error);
      Swal.fire('Error', error.response?.data?.message || 'Failed to update stock item', 'error');
    }
  };

  const handleToggleStatus = async (item) => {
    const newStatus = item.status === 'active' ? 'inactive' : 'active';
    try {
      await api.put(`/stock-items/update-status/${item._id}`, { status: newStatus });
      setStockItems(prev => prev.map(i => i._id === item._id ? { ...i, status: newStatus } : i));
      fetchStatistics();
      Swal.fire({
        icon: 'success',
        title: newStatus === 'active' ? 'Item Enabled' : 'Item Disabled',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 2000
      });
    } catch (error) {
      console.error('Error updating status:', error);
      Swal.fire('Error', 'Failed to update status', 'error');
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({ category: null, brand: null, status: null });
    setSearchTerm('');
    setCurrentPage(1);
  };

  const getTotalStock = (item) => {
    return item.stock?.reduce((acc, stock) => ({
      freshQty: acc.freshQty + (stock.freshQuantity || 0),
      damagedQty: acc.damagedQty + (stock.damagedQuantity || 0)
    }), { freshQty: 0, damagedQty: 0 }) || { freshQty: 0, damagedQty: 0 };
  };

  // QR Code handlers
  const handleShowQR = (item) => {
    setQrItem(item);
    setShowQRModal(true);
  };

  const getQRSvgElement = () => document.getElementById('stock-item-qr-code-svg');

  const svgToCanvas = (svg, callback) => {
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      canvas.width = img.width * 2;
      canvas.height = img.height * 2;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      callback(canvas);
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const downloadQRAsPNG = () => {
    const svg = getQRSvgElement();
    if (!svg) return;
    svgToCanvas(svg, (canvas) => {
      const link = document.createElement('a');
      link.download = `${qrItem.name || qrItem.id}_QR.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    });
  };

  const downloadQRAsJPG = () => {
    const svg = getQRSvgElement();
    if (!svg) return;
    svgToCanvas(svg, (canvas) => {
      const link = document.createElement('a');
      link.download = `${qrItem.name || qrItem.id}_QR.jpg`;
      link.href = canvas.toDataURL('image/jpeg', 0.95);
      link.click();
    });
  };

  const downloadQRAsSVG = () => {
    const svg = getQRSvgElement();
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([svgData], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.download = `${qrItem.name || qrItem.id}_QR.svg`;
    link.href = url;
    link.click();
    URL.revokeObjectURL(url);
  };

  const downloadQRAsPDF = (includeDetails = false) => {
    const svg = getQRSvgElement();
    if (!svg) return;
    svgToCanvas(svg, (canvas) => {
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: [45, 65] });

      if (includeDetails) {
        const itemName = qrItem.name || 'Item';
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'bold');
        const nameWidth = pdf.getTextWidth(itemName);
        pdf.text(itemName, Math.max((45 - nameWidth) / 2, 2), 8);
        pdf.addImage(imgData, 'PNG', (45 - 25) / 2, 12, 25, 25);
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        const idText = `ID: ${qrItem.id}`;
        pdf.text(idText, (45 - pdf.getTextWidth(idText)) / 2, 42);
        if (qrItem.category?.name) {
          const catText = qrItem.category.name;
          pdf.text(catText, (45 - pdf.getTextWidth(catText)) / 2, 47);
        }
        if (qrItem.brand?.name) {
          const brandText = qrItem.brand.name;
          pdf.text(brandText, (45 - pdf.getTextWidth(brandText)) / 2, 52);
        }
        pdf.save(`${qrItem.name || qrItem.id}_QR_with_details.pdf`);
      } else {
        pdf.addImage(imgData, 'PNG', (45 - 35) / 2, 15, 35, 35);
        pdf.save(`${qrItem.name || qrItem.id}_QR.pdf`);
      }
    });
  };

  const StatusBadge = ({ status }) => (
    <span className={`px-2 py-1 rounded-full text-xs font-bold ${
      status === 'active' 
        ? darkMode ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-800'
        : darkMode ? 'bg-red-900 text-red-300' : 'bg-red-100 text-red-800'
    }`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );

  // Selection handling
  const handleSelectAll = () => {
    if (selectedItems.length === stockItems.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(stockItems.map(i => i._id));
    }
  };

  const handleSelectItem = (itemId) => {
    setSelectedItems(prev => prev.includes(itemId) ? prev.filter(id => id !== itemId) : [...prev, itemId]);
  };

  // Export Stock to Excel
  const handleExportStock = async () => {
    try {
      setExportLoading(true);
      const params = new URLSearchParams({
        userId: user?.user?._id,
        ...(exportStockFilters.brand && { brand: exportStockFilters.brand }),
        ...(exportStockFilters.category && { category: exportStockFilters.category }),
        ...(exportStockFilters.status && { status: exportStockFilters.status }),
        ...(exportStockFilters.location && { location: exportStockFilters.location })
      });
      
      const response = await api.get(`/stock-items/export-all-excel?${params}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `stock_items_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      Swal.fire('Success', 'Stock data exported successfully', 'success');
      setShowExportStockModal(false);
    } catch (error) {
      console.error('Error exporting stock:', error);
      Swal.fire('Error', 'Failed to export stock data', 'error');
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportStockFilterChange = (field, value) => {
    setExportStockFilters(prev => ({ ...prev, [field]: value }));
  };

  // Stock Movement Export handler
  const handleStockMovementExport = async () => {
    if (!stockMovementFilters.location) {
      Swal.fire('Warning', 'Please select a location', 'warning');
      return;
    }
    try {
      setStockMovementLoading(true);
      const params = new URLSearchParams({
        userId: user?.user?._id,
        startDate: stockMovementFilters.startDate,
        endDate: stockMovementFilters.endDate,
        location: stockMovementFilters.location,
        ...(stockMovementFilters.brand && { brand: stockMovementFilters.brand }),
        ...(stockMovementFilters.category && { category: stockMovementFilters.category })
      });
      const response = await api.get(`/stock-items/export-stock-movement?${params}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Stock_Items_Movement_${stockMovementFilters.startDate}_to_${stockMovementFilters.endDate}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      Swal.fire('Success', 'Stock movement exported successfully', 'success');
      setShowStockMovementModal(false);
    } catch (error) {
      console.error('Error exporting stock movement:', error);
      Swal.fire('Error', 'Failed to export stock movement data', 'error');
    } finally {
      setStockMovementLoading(false);
    }
  };

  // Fetch items for bulk QR export
  const fetchBulkQRItems = async () => {
    try {
      setLoadingBulkQR(true);
      const params = new URLSearchParams({
        companyId: getCompanyId(),
        limit: 5000,
        ...(bulkQRFilters.brand && { brand: bulkQRFilters.brand }),
        ...(bulkQRFilters.category && { category: bulkQRFilters.category }),
        ...(bulkQRFilters.status && { status: bulkQRFilters.status })
      });

      const response = await api.get(`/stock-items/management/all?${params}`);
      if (response.data.success) {
        setBulkQRItems(response.data.data.stockItems);
      }
    } catch (error) {
      console.error('Error fetching items for bulk QR:', error);
      Swal.fire('Error', 'Failed to fetch stock items', 'error');
    } finally {
      setLoadingBulkQR(false);
    }
  };

  // Generate bulk QR codes as ZIP
  const generateBulkQRZip = async () => {
    if (bulkQRItems.length === 0) {
      Swal.fire('Warning', 'No items found with the selected filters', 'warning');
      return;
    }

    try {
      setExportLoading(true);
      Swal.fire({
        title: 'Generating QR Codes',
        html: 'Creating QR codes for <b>0</b> / <b>' + bulkQRItems.length + '</b> items...',
        allowOutsideClick: false,
        didOpen: () => { Swal.showLoading(); }
      });

      const zip = new JSZip();

      for (let i = 0; i < bulkQRItems.length; i++) {
        const item = bulkQRItems[i];
        Swal.update({ html: `Creating QR codes for <b>${i + 1}</b> / <b>${bulkQRItems.length}</b> items...` });

        const folderName = `${item.name.replace(/[/\\?%*:|"<>]/g, '-')}_${item.id}`;
        const itemFolder = zip.folder(folderName);
        const qrValue = item._id;

        try {
          const pngDataUrl = await QRCodeLib.toDataURL(qrValue, { width: 512, margin: 2, errorCorrectionLevel: 'H', type: 'image/png' });
          itemFolder.file(`${item.id}_QR.png`, pngDataUrl.split(',')[1], { base64: true });
        } catch (err) { console.error('Error generating PNG for', item.id, err); }

        try {
          const jpgDataUrl = await QRCodeLib.toDataURL(qrValue, { width: 512, margin: 2, errorCorrectionLevel: 'H', type: 'image/jpeg', quality: 0.95 });
          itemFolder.file(`${item.id}_QR.jpg`, jpgDataUrl.split(',')[1], { base64: true });
        } catch (err) { console.error('Error generating JPG for', item.id, err); }

        try {
          const svgString = await QRCodeLib.toString(qrValue, { type: 'svg', width: 512, margin: 2, errorCorrectionLevel: 'H' });
          itemFolder.file(`${item.id}_QR.svg`, svgString);
        } catch (err) { console.error('Error generating SVG for', item.id, err); }

        try {
          const pdfQROnly = new jsPDF({ orientation: 'portrait', unit: 'mm', format: [45, 65] });
          const qrDataUrl = await QRCodeLib.toDataURL(qrValue, { width: 300, margin: 1, errorCorrectionLevel: 'M' });
          pdfQROnly.addImage(qrDataUrl, 'PNG', (45 - 35) / 2, 15, 35, 35);
          itemFolder.file(`${item.id}_QR_Only.pdf`, pdfQROnly.output('blob'));
        } catch (err) { console.error('Error generating PDF for', item.id, err); }

        try {
          const pdfWithDetails = new jsPDF({ orientation: 'portrait', unit: 'mm', format: [45, 65] });
          const qrDataUrl = await QRCodeLib.toDataURL(qrValue, { width: 300, margin: 1, errorCorrectionLevel: 'M' });
          const itemName = item.name || 'Item';
          pdfWithDetails.setFontSize(10);
          pdfWithDetails.setFont('helvetica', 'bold');
          const nameWidth = pdfWithDetails.getTextWidth(itemName);
          pdfWithDetails.text(itemName, Math.max((45 - nameWidth) / 2, 2), 8);
          pdfWithDetails.addImage(qrDataUrl, 'PNG', (45 - 25) / 2, 12, 25, 25);
          pdfWithDetails.setFontSize(8);
          pdfWithDetails.setFont('helvetica', 'normal');
          const idText = `ID: ${item.id}`;
          pdfWithDetails.text(idText, (45 - pdfWithDetails.getTextWidth(idText)) / 2, 42);
          if (item.category?.name) {
            const catText = item.category.name;
            pdfWithDetails.text(catText, (45 - pdfWithDetails.getTextWidth(catText)) / 2, 47);
          }
          itemFolder.file(`${item.id}_QR_Details.pdf`, pdfWithDetails.output('blob'));
        } catch (err) { console.error('Error generating PDF with details for', item.id, err); }

        if (i % 10 === 0) await new Promise(resolve => setTimeout(resolve, 10));
      }

      Swal.update({ html: 'Compressing files...' });
      const zipBlob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE', compressionOptions: { level: 6 } });
      saveAs(zipBlob, `StockItems_QR_Codes_${new Date().toISOString().split('T')[0]}.zip`);

      await Swal.fire({ title: 'Success!', text: `Generated QR codes for ${bulkQRItems.length} items`, icon: 'success', timer: 3000, showConfirmButton: false });
      setShowBulkQRModal(false);
    } catch (error) {
      console.error('Error generating bulk QR ZIP:', error);
      Swal.fire('Error', 'Failed to generate QR codes: ' + error.message, 'error');
    } finally {
      setExportLoading(false);
    }
  };

  const handleBulkQRFilterChange = (field, value) => {
    setBulkQRFilters(prev => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    if (showBulkQRModal) { fetchBulkQRItems(); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkQRFilters, showBulkQRModal]);

  return (
    <div className={`p-6 min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h1 className={`text-3xl font-bold flex items-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            <FaCubes className="mr-3 text-blue-600" />
            Stock Items Management
          </h1>
          <div className="flex items-center space-x-2">
            {/* View Mode Toggle */}
            <div className={`flex border rounded-lg overflow-hidden ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}>
              <button
                onClick={() => setViewMode('cards')}
                className={`px-3 py-2 flex items-center space-x-1 ${viewMode === 'cards' ? 'bg-blue-600 text-white' : (darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white text-gray-700 hover:bg-gray-100')}`}
                title="Cards View"
              >
                <FaTh className="w-4 h-4" />
                <span className="hidden sm:inline">Cards</span>
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`px-3 py-2 flex items-center space-x-1 ${viewMode === 'table' ? 'bg-blue-600 text-white' : (darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white text-gray-700 hover:bg-gray-100')}`}
                title="Table View"
              >
                <FaList className="w-4 h-4" />
                <span className="hidden sm:inline">Table</span>
              </button>
            </div>

            <button
              onClick={() => setShowExportStockModal(true)}
              disabled={exportLoading}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center space-x-2 disabled:opacity-50"
              title="Export Stock"
            >
              <FaFileExport className="w-4 h-4" />
              <span className="hidden sm:inline">Export Stock</span>
            </button>

            <button
              onClick={() => setShowStockMovementModal(true)}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center space-x-2"
              title="Export Stock Movement"
            >
              <FaTruck className="w-4 h-4" />
              <span className="hidden sm:inline">Stock Movement</span>
            </button>

            <button
              onClick={() => setShowBulkQRModal(true)}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center space-x-2"
              title="Export QR Codes"
            >
              <FaQrcode className="w-4 h-4" />
              <span className="hidden sm:inline">Export QR Codes</span>
            </button>

            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
            >
              <FaSync className="w-4 h-4" />
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
          <div className={`p-4 rounded-lg shadow border-l-4 border-blue-500 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total Items</p>
                <p className="text-2xl font-bold text-blue-600">{statistics.totalItems || 0}</p>
              </div>
              <FaCubes className="w-8 h-8 text-blue-500" />
            </div>
          </div>
          
          <div className={`p-4 rounded-lg shadow border-l-4 border-green-500 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Active</p>
                <p className="text-2xl font-bold text-green-600">{statistics.activeItems || 0}</p>
              </div>
              <FaToggleOn className="w-8 h-8 text-green-500" />
            </div>
          </div>

          <div className={`p-4 rounded-lg shadow border-l-4 border-red-500 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Inactive</p>
                <p className="text-2xl font-bold text-red-600">{statistics.inactiveItems || 0}</p>
              </div>
              <FaToggleOff className="w-8 h-8 text-red-500" />
            </div>
          </div>

          <div className={`p-4 rounded-lg shadow border-l-4 border-orange-500 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>With Stock</p>
                <p className="text-2xl font-bold text-orange-600">{statistics.itemsWithStock || 0}</p>
              </div>
              <FaBox className="w-8 h-8 text-orange-500" />
            </div>
          </div>

          <div className={`p-4 rounded-lg shadow border-l-4 border-indigo-500 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Fresh Qty</p>
                <p className="text-2xl font-bold text-indigo-600">
                  {statistics.stockSummary?.totalFreshQuantity || 0}
                </p>
              </div>
              <FaWarehouse className="w-8 h-8 text-indigo-500" />
            </div>
          </div>

          <div className={`p-4 rounded-lg shadow border-l-4 border-yellow-500 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Damaged Qty</p>
                <p className="text-2xl font-bold text-yellow-600">
                  {statistics.stockSummary?.totalDamagedQuantity || 0}
                </p>
              </div>
              <FaExclamationTriangle className="w-8 h-8 text-yellow-500" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className={`p-4 rounded-lg shadow mb-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="lg:col-span-2">
            <div className="relative">
              <input
                type="text"
                placeholder="Search stock items..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'border-gray-300'}`}
              />
              <FaSearch className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
            </div>
          </div>

          <div>
            <Select
              placeholder="Category"
              isClearable
              value={filters.category ? { value: filters.category, label: categories.find(c => c._id === filters.category)?.name } : null}
              onChange={(option) => handleFilterChange('category', option?.value || null)}
              options={categories.map(cat => ({ value: cat._id, label: cat.name }))}
              styles={darkSelectStyles}
            />
          </div>

          <div>
            <Select
              placeholder="Brand"
              isClearable
              value={filters.brand ? { value: filters.brand, label: brands.find(b => b._id === filters.brand)?.name } : null}
              onChange={(option) => handleFilterChange('brand', option?.value || null)}
              options={brands.map(brand => ({ value: brand._id, label: brand.name }))}
              styles={darkSelectStyles}
            />
          </div>

          <div>
            <Select
              placeholder="Status"
              isClearable
              value={filters.status ? { value: filters.status, label: filters.status.charAt(0).toUpperCase() + filters.status.slice(1) } : null}
              onChange={(option) => handleFilterChange('status', option?.value || null)}
              options={[
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Inactive' }
              ]}
              styles={darkSelectStyles}
            />
          </div>
        </div>

        <div className="flex justify-between items-center mt-4">
          <button
            onClick={clearFilters}
            className={`px-4 py-2 flex items-center space-x-2 ${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-800'}`}
          >
            <FaTimes className="w-4 h-4" />
            <span>Clear Filters</span>
          </button>
          <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Showing {stockItems.length} of {totalRecords} items
          </div>
        </div>
      </div>

      {/* Table View */}
      {viewMode === 'table' && (
      <div className={`rounded-lg shadow overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="overflow-x-auto">
          <table className={`min-w-full divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
            <thead className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
              <tr>
                <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider w-12 ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                  <button onClick={handleSelectAll} className={`p-1 rounded ${darkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-200'}`} title={selectedItems.length === stockItems.length ? 'Deselect All' : 'Select All'}>
                    {selectedItems.length === stockItems.length && stockItems.length > 0 ? <FaCheckSquare className="w-5 h-5 text-blue-600" /> : <FaSquare className="w-5 h-5 text-gray-400" />}
                  </button>
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Item Details</th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Brand / Category</th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Unit</th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Status</th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Stock Summary</th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Actions</th>
              </tr>
            </thead>
            <tbody className={`divide-y ${darkMode ? 'bg-gray-800 divide-gray-700' : 'bg-white divide-gray-200'}`}>
              {loading ? (
                <tr>
                  <td colSpan="7" className="text-center py-8">
                    <div className="inline-flex items-center space-x-2">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                      <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>Loading stock items...</span>
                    </div>
                  </td>
                </tr>
              ) : stockItems.length === 0 ? (
                <tr>
                  <td colSpan="7" className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    No stock items found matching your criteria
                  </td>
                </tr>
              ) : (
                stockItems.map((item) => {
                  const totalStock = getTotalStock(item);
                  return (
                    <tr key={item._id} className={`${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'} ${selectedItems.includes(item._id) ? (darkMode ? 'bg-blue-900/30' : 'bg-blue-50') : ''}`}>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <button onClick={() => handleSelectItem(item._id)} className="p-1 hover:bg-gray-200 rounded">
                          {selectedItems.includes(item._id) ? <FaCheckSquare className="w-5 h-5 text-blue-600" /> : <FaSquare className="w-5 h-5 text-gray-400" />}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className={`text-sm font-medium ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>{item.name}</div>
                          <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>ID: {item.id} | Part: {item.partNumber || 'N/A'}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`text-sm ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                          <div>{item.brand?.name || 'N/A'}</div>
                          <div className={darkMode ? 'text-gray-400' : 'text-gray-500'}>{item.category?.name || 'N/A'}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`text-sm ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>{item.unitOfMeasure || 'N/A'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <StatusBadge status={item.status} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`text-xs space-y-1 ${darkMode ? 'text-gray-300' : ''}`}>
                          <div className="flex items-center space-x-2">
                            <FaBox className="w-3 h-3 text-green-600" />
                            <span>Fresh: {totalStock.freshQty}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <FaExclamationTriangle className="w-3 h-3 text-orange-600" />
                            <span>Damaged: {totalStock.damagedQty}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => { setSelectedItem(item); setShowEditModal(true); }}
                            className={`p-2 rounded ${darkMode ? 'text-blue-400 hover:bg-blue-900/30' : 'text-blue-600 hover:bg-blue-100'}`}
                            title="Manage"
                          >
                            <FaEdit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleShowQR(item)}
                            className={`p-2 rounded ${darkMode ? 'text-purple-400 hover:bg-purple-900/30' : 'text-purple-600 hover:bg-purple-100'}`}
                            title="QR Code"
                          >
                            <FaQrcode className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleToggleStatus(item)}
                            className={`p-2 rounded ${item.status === 'active' ? (darkMode ? 'text-green-400 hover:bg-green-900/30' : 'text-green-600 hover:bg-green-100') : (darkMode ? 'text-red-400 hover:bg-red-900/30' : 'text-red-600 hover:bg-red-100')}`}
                            title={item.status === 'active' ? 'Disable' : 'Enable'}
                          >
                            {item.status === 'active' ? <FaToggleOn className="w-4 h-4" /> : <FaToggleOff className="w-4 h-4" />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className={`px-4 py-3 flex items-center justify-between border-t ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Page <span className="font-medium">{currentPage}</span> of <span className="font-medium">{totalPages}</span>
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className={`relative inline-flex items-center px-4 py-2 rounded-l-md border text-sm font-medium disabled:opacity-50 ${darkMode ? 'border-gray-600 bg-gray-700 text-gray-300 hover:bg-gray-600' : 'border-gray-300 bg-white text-gray-500 hover:bg-gray-50'}`}
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className={`relative inline-flex items-center px-4 py-2 rounded-r-md border text-sm font-medium disabled:opacity-50 ${darkMode ? 'border-gray-600 bg-gray-700 text-gray-300 hover:bg-gray-600' : 'border-gray-300 bg-white text-gray-500 hover:bg-gray-50'}`}
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
      )}

      {/* Cards View */}
      {viewMode === 'cards' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {loading ? (
            <div className="col-span-full flex justify-center py-8">
              <div className="inline-flex items-center space-x-2">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>Loading stock items...</span>
              </div>
            </div>
          ) : stockItems.length === 0 ? (
            <div className={`col-span-full text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              No stock items found matching your criteria
            </div>
          ) : (
            stockItems.map((item) => {
              const totalStock = getTotalStock(item);
              return (
                <div key={item._id} className={`rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow ${darkMode ? 'bg-gray-800' : 'bg-white'} ${selectedItems.includes(item._id) ? 'ring-2 ring-blue-500' : ''}`}>
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <button onClick={() => handleSelectItem(item._id)} className={`p-1 rounded ${darkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-200'}`}>
                        {selectedItems.includes(item._id) ? <FaCheckSquare className="w-5 h-5 text-blue-600" /> : <FaSquare className="w-5 h-5 text-gray-400" />}
                      </button>
                      <StatusBadge status={item.status} />
                    </div>
                    
                    <h3 className={`text-lg font-semibold mb-1 ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>{item.name}</h3>
                    <p className={`text-sm mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>ID: {item.id}</p>
                    
                    <div className={`text-sm mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      <div>{item.brand?.name || 'N/A'} - {item.category?.name || 'N/A'}</div>
                      <div className={darkMode ? 'text-gray-500' : 'text-gray-400'}>Unit: {item.unitOfMeasure || 'N/A'}</div>
                    </div>
                    
                    <div className={`border-t pt-3 mt-3 ${darkMode ? 'border-gray-700' : ''}`}>
                      <div className="flex justify-between text-xs">
                        <span className="text-green-600">Fresh: {totalStock.freshQty}</span>
                        <span className="text-orange-600">Damaged: {totalStock.damagedQty}</span>
                      </div>
                    </div>
                    
                    <div className={`flex justify-center space-x-2 mt-3 border-t pt-3 ${darkMode ? 'border-gray-700' : ''}`}>
                      <button
                        onClick={() => { setSelectedItem(item); setShowEditModal(true); }}
                        className={`p-2 rounded ${darkMode ? 'text-blue-400 hover:bg-blue-900/30' : 'text-blue-600 hover:bg-blue-100'}`}
                        title="Manage"
                      >
                        <FaEdit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleShowQR(item)}
                        className={`p-2 rounded ${darkMode ? 'text-purple-400 hover:bg-purple-900/30' : 'text-purple-600 hover:bg-purple-100'}`}
                        title="QR Code"
                      >
                        <FaQrcode className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Cards View Pagination */}
      {viewMode === 'cards' && totalPages > 1 && (
        <div className="mt-4 flex justify-center">
          <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className={`relative inline-flex items-center px-4 py-2 rounded-l-md border text-sm font-medium disabled:opacity-50 ${darkMode ? 'border-gray-600 bg-gray-700 text-gray-300 hover:bg-gray-600' : 'border-gray-300 bg-white text-gray-500 hover:bg-gray-50'}`}
            >
              Previous
            </button>
            <span className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${darkMode ? 'border-gray-600 bg-gray-700 text-gray-300' : 'border-gray-300 bg-gray-50 text-gray-700'}`}>
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className={`relative inline-flex items-center px-4 py-2 rounded-r-md border text-sm font-medium disabled:opacity-50 ${darkMode ? 'border-gray-600 bg-gray-700 text-gray-300 hover:bg-gray-600' : 'border-gray-300 bg-white text-gray-500 hover:bg-gray-50'}`}
            >
              Next
            </button>
          </nav>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`rounded-lg p-6 max-w-6xl w-full mx-4 max-h-screen overflow-y-auto ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="flex items-center justify-between mb-6">
              <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Stock Item Management</h2>
              <button onClick={() => { setShowEditModal(false); setIsEditingItem(false); }} className={darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-600'}>
                <FaTimes className="w-6 h-6" />
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Item Details */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : ''}`}>Item Information</h3>
                  {!isEditingItem ? (
                    <button onClick={handleEditItem} className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 flex items-center space-x-2">
                      <FaEdit className="w-3 h-3" /><span>Edit</span>
                    </button>
                  ) : (
                    <div className="flex space-x-2">
                      <button onClick={handleSaveItem} className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 flex items-center space-x-2">
                        <FaSave className="w-3 h-3" /><span>Save</span>
                      </button>
                      <button onClick={handleCancelEdit} className="px-3 py-1 bg-gray-600 text-white text-sm rounded hover:bg-gray-700">Cancel</button>
                    </div>
                  )}
                </div>
                <div className="space-y-4">
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Name</label>
                    {isEditingItem ? (
                      <input type="text" value={editFormData.name} onChange={(e) => setEditFormData(prev => ({ ...prev, name: e.target.value }))} className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`} />
                    ) : (
                      <div className={`p-2 border rounded ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-gray-50'}`}>{selectedItem.name}</div>
                    )}
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Part Number</label>
                    {isEditingItem ? (
                      <input type="text" value={editFormData.partNumber} onChange={(e) => setEditFormData(prev => ({ ...prev, partNumber: e.target.value }))} className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`} placeholder="Enter part number" />
                    ) : (
                      <div className={`p-2 border rounded ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-gray-50'}`}>{selectedItem.partNumber || 'Not set'}</div>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Brand</label>
                      {isEditingItem ? (
                        <Select
                          value={brands.find(b => b._id === editFormData.brand) ? { value: editFormData.brand, label: brands.find(b => b._id === editFormData.brand)?.name } : null}
                          onChange={(option) => setEditFormData(prev => ({ ...prev, brand: option?.value || '' }))}
                          options={brands.map(b => ({ value: b._id, label: b.name }))}
                          isClearable placeholder="Select brand" styles={darkSelectStyles}
                        />
                      ) : (
                        <div className={`p-2 border rounded ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-gray-50'}`}>{selectedItem.brand?.name || 'Not set'}</div>
                      )}
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Category</label>
                      {isEditingItem ? (
                        <Select
                          value={categories.find(c => c._id === editFormData.category) ? { value: editFormData.category, label: categories.find(c => c._id === editFormData.category)?.name } : null}
                          onChange={(option) => setEditFormData(prev => ({ ...prev, category: option?.value || '' }))}
                          options={categories.map(c => ({ value: c._id, label: c.name }))}
                          isClearable placeholder="Select category" styles={darkSelectStyles}
                        />
                      ) : (
                        <div className={`p-2 border rounded ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-gray-50'}`}>{selectedItem.category?.name || 'Not set'}</div>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Unit of Measure</label>
                      {isEditingItem ? (
                        <input type="text" value={editFormData.unitOfMeasure} onChange={(e) => setEditFormData(prev => ({ ...prev, unitOfMeasure: e.target.value }))} className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`} placeholder="e.g., Pcs, Kg, Ltr" />
                      ) : (
                        <div className={`p-2 border rounded ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-gray-50'}`}>{selectedItem.unitOfMeasure || 'Not set'}</div>
                      )}
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Status</label>
                      {isEditingItem ? (
                        <Select
                          value={{ value: editFormData.status, label: editFormData.status.charAt(0).toUpperCase() + editFormData.status.slice(1) }}
                          onChange={(option) => setEditFormData(prev => ({ ...prev, status: option?.value || 'active' }))}
                          options={[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]}
                          placeholder="Select status" styles={darkSelectStyles}
                        />
                      ) : (
                        <div className={`p-2 border rounded ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-gray-50'}`}>
                          {selectedItem.status?.charAt(0).toUpperCase() + selectedItem.status?.slice(1) || 'Active'}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Stock Management */}
              <div>
                <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : ''}`}>Stock by Location</h3>
                <div className="space-y-4">
                  {locations.length === 0 ? (
                    <div className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      <p>No locations available. Please add locations first.</p>
                    </div>
                  ) : (
                    locations.map((location) => {
                      const existingStock = selectedItem.stock?.find(s => s.location?._id === location._id || s.location === location._id);
                      return (
                        <div key={location._id} className={`border rounded-lg p-4 ${darkMode ? 'border-gray-600' : ''}`}>
                          <div className="flex items-center justify-between mb-3">
                            <h4 className={`font-medium ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>{location.name || location.locationName}</h4>
                            <button onClick={() => handleStockEdit(selectedItem, location)} className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700">Edit Stock</button>
                          </div>
                          {existingStock ? (
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              <div className="flex items-center space-x-2">
                                <FaBox className="w-3 h-3 text-green-600" />
                                <span>Fresh: {existingStock.freshQuantity || 0}</span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <FaExclamationTriangle className="w-3 h-3 text-orange-600" />
                                <span>Damaged: {existingStock.damagedQuantity || 0}</span>
                              </div>
                            </div>
                          ) : (
                            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No stock recorded</p>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stock Edit Modal */}
      {showStockModal && selectedItem && selectedLocation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`rounded-lg p-6 max-w-md w-full mx-4 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="flex items-center justify-between mb-6">
              <h2 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Edit Stock - {selectedLocation.locationName || selectedLocation.name}
              </h2>
              <button onClick={() => setShowStockModal(false)} className="text-gray-400 hover:text-gray-600">
                <FaTimes className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`flex items-center space-x-2 text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <FaBox className="w-4 h-4 text-green-600" />
                    <span>Fresh Quantity</span>
                  </label>
                  <input
                    type="number" min="0" step="any"
                    value={stockValues.freshQuantity}
                    onChange={(e) => setStockValues(prev => ({ ...prev, freshQuantity: parseFloat(e.target.value) || 0 }))}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                  />
                </div>
                <div>
                  <label className={`flex items-center space-x-2 text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <FaExclamationTriangle className="w-4 h-4 text-orange-600" />
                    <span>Damaged Quantity</span>
                  </label>
                  <input
                    type="number" min="0" step="any"
                    value={stockValues.damagedQuantity}
                    onChange={(e) => setStockValues(prev => ({ ...prev, damagedQuantity: parseFloat(e.target.value) || 0 }))}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                  />
                </div>
              </div>

              <div className={`flex justify-end space-x-3 pt-4 border-t ${darkMode ? 'border-gray-700' : ''}`}>
                <button onClick={() => setShowStockModal(false)} className={`px-4 py-2 border rounded-lg ${darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>Cancel</button>
                <button onClick={handleStockUpdate} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2">
                  <FaSave className="w-4 h-4" /><span>Update Stock</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* QR Code Modal */}
      {showQRModal && qrItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`rounded-xl shadow-2xl max-w-md w-full mx-4 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className={`border-b px-6 py-4 flex justify-between items-center ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className={`text-xl font-semibold flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                <FaQrcode className="text-purple-600" /> QR Code
              </h3>
              <button onClick={() => setShowQRModal(false)} className={`p-2 rounded-lg transition-colors ${darkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}>
                <FaTimes className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 text-center space-y-4">
              <div className="bg-white p-4 rounded-lg border border-gray-200 inline-block">
                <QRCode id="stock-item-qr-code-svg" value={qrItem._id} size={200} bgColor="#ffffff" fgColor="#000000" />
              </div>
              
              <div>
                <h4 className={`font-medium mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{qrItem.name}</h4>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>ID: {qrItem.id}</p>
                {qrItem.category?.name && <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{qrItem.category.name}</p>}
              </div>

              <div className={`space-y-3 pt-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <p className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Export as:</p>
                <div className="grid grid-cols-3 gap-2">
                  <button onClick={downloadQRAsPNG} className="flex items-center justify-center gap-2 px-3 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700"><FaFileImage className="w-4 h-4" />PNG</button>
                  <button onClick={downloadQRAsJPG} className="flex items-center justify-center gap-2 px-3 py-2 bg-orange-600 text-white text-sm rounded-lg hover:bg-orange-700"><FaFileImage className="w-4 h-4" />JPG</button>
                  <button onClick={downloadQRAsSVG} className="flex items-center justify-center gap-2 px-3 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700"><FaFileImage className="w-4 h-4" />SVG</button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={() => downloadQRAsPDF(false)} className="flex items-center justify-center gap-2 px-3 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700"><FaFilePdf className="w-4 h-4" />PDF (QR Only)</button>
                  <button onClick={() => downloadQRAsPDF(true)} className="flex items-center justify-center gap-2 px-3 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700"><FaFilePdf className="w-4 h-4" />PDF with Details</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk QR Export Modal */}
      {showBulkQRModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`rounded-xl shadow-2xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className={`border-b px-6 py-4 flex justify-between items-center flex-shrink-0 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className={`text-xl font-semibold flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                <FaQrcode className="text-purple-600" /> Export QR Codes (Bulk)
              </h3>
              <button onClick={() => setShowBulkQRModal(false)} className={`p-2 rounded-lg transition-colors ${darkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}>
                <FaTimes className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto flex-grow">
              <p className={`mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Select filters to generate QR codes for matching items. Leave filters empty to include all.</p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Brand</label>
                  <Select placeholder="All Brands" isClearable value={bulkQRFilters.brand ? { value: bulkQRFilters.brand, label: brands.find(b => b._id === bulkQRFilters.brand)?.name } : null} onChange={(option) => handleBulkQRFilterChange('brand', option?.value || null)} options={brands.map(b => ({ value: b._id, label: b.name }))} styles={darkSelectStyles} />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Category</label>
                  <Select placeholder="All Categories" isClearable value={bulkQRFilters.category ? { value: bulkQRFilters.category, label: categories.find(c => c._id === bulkQRFilters.category)?.name } : null} onChange={(option) => handleBulkQRFilterChange('category', option?.value || null)} options={categories.map(c => ({ value: c._id, label: c.name }))} styles={darkSelectStyles} />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Status</label>
                  <Select placeholder="All Statuses" isClearable value={bulkQRFilters.status ? { value: bulkQRFilters.status, label: bulkQRFilters.status.charAt(0).toUpperCase() + bulkQRFilters.status.slice(1) } : null} onChange={(option) => handleBulkQRFilterChange('status', option?.value || null)} options={[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]} styles={darkSelectStyles} />
                </div>
              </div>

              <div className={`rounded-lg p-4 mb-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <span className={darkMode ? 'text-gray-300' : 'text-gray-600'}>Matching items: </span>
                    <span className="font-bold text-lg text-blue-600">
                      {loadingBulkQR ? <span className="inline-flex items-center"><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>Loading...</span> : bulkQRItems.length}
                    </span>
                  </div>
                </div>
              </div>

              {bulkQRItems.length > 0 && (
                <div className={`border rounded-lg max-h-40 overflow-y-auto ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                  <div className={`p-2 border-b sticky top-0 ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                    <span className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Preview (first 10 items)</span>
                  </div>
                  <div className={`divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-100'}`}>
                    {bulkQRItems.slice(0, 10).map((item) => (
                      <div key={item._id} className="px-3 py-2 text-sm flex justify-between items-center">
                        <span className={`font-medium ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>{item.name}</span>
                        <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>{item.id}</span>
                      </div>
                    ))}
                    {bulkQRItems.length > 10 && (
                      <div className={`px-3 py-2 text-sm text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        ... and {bulkQRItems.length - 10} more items
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className={`border-t px-6 py-4 flex justify-between items-center flex-shrink-0 ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
              <button onClick={() => setBulkQRFilters({ brand: null, category: null, status: 'active' })} className={`px-4 py-2 flex items-center space-x-2 ${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-800'}`}>
                <FaTimes className="w-4 h-4" /><span>Clear Filters</span>
              </button>
              <button onClick={generateBulkQRZip} disabled={bulkQRItems.length === 0 || exportLoading} className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed">
                {exportLoading ? (<><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div><span>Generating...</span></>) : (<><FaQrcode className="w-4 h-4" /><span>Generate {bulkQRItems.length} QR Codes (ZIP)</span></>)}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export Stock Modal */}
      {showExportStockModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`rounded-xl shadow-2xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className={`border-b px-6 py-4 flex justify-between items-center flex-shrink-0 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className={`text-xl font-semibold flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                <FaFileExport className="text-green-600" /> Export Stock to Excel
              </h3>
              <button onClick={() => setShowExportStockModal(false)} className={`p-2 rounded-lg transition-colors ${darkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}>
                <FaTimes className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto flex-grow">
              <p className={`mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Select filters to export stock data. Leave filters empty to export all.</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Brand</label>
                  <Select placeholder="All Brands" isClearable value={exportStockFilters.brand ? { value: exportStockFilters.brand, label: brands.find(b => b._id === exportStockFilters.brand)?.name } : null} onChange={(option) => handleExportStockFilterChange('brand', option?.value || null)} options={brands.map(b => ({ value: b._id, label: b.name }))} styles={darkSelectStyles} />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Category</label>
                  <Select placeholder="All Categories" isClearable value={exportStockFilters.category ? { value: exportStockFilters.category, label: categories.find(c => c._id === exportStockFilters.category)?.name } : null} onChange={(option) => handleExportStockFilterChange('category', option?.value || null)} options={categories.map(c => ({ value: c._id, label: c.name }))} styles={darkSelectStyles} />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Status</label>
                  <Select placeholder="All Statuses" isClearable value={exportStockFilters.status ? { value: exportStockFilters.status, label: exportStockFilters.status.charAt(0).toUpperCase() + exportStockFilters.status.slice(1) } : null} onChange={(option) => handleExportStockFilterChange('status', option?.value || null)} options={[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]} styles={darkSelectStyles} />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Location (Optional)</label>
                  <Select placeholder="All Locations" isClearable value={exportStockFilters.location ? { value: exportStockFilters.location, label: locations.find(l => l._id === exportStockFilters.location)?.name || locations.find(l => l._id === exportStockFilters.location)?.locationName } : null} onChange={(option) => handleExportStockFilterChange('location', option?.value || null)} options={locations.map(loc => ({ value: loc._id, label: loc.name || loc.locationName }))} styles={darkSelectStyles} />
                </div>
              </div>
            </div>

            <div className={`border-t px-6 py-4 flex justify-between items-center flex-shrink-0 ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
              <button onClick={() => setExportStockFilters({ brand: null, category: null, status: 'active', location: null })} className={`px-4 py-2 flex items-center space-x-2 ${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-800'}`}>
                <FaTimes className="w-4 h-4" /><span>Clear Filters</span>
              </button>
              <button onClick={handleExportStock} disabled={exportLoading} className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed">
                {exportLoading ? (<><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div><span>Exporting...</span></>) : (<><FaFileExcel className="w-4 h-4" /><span>Export Excel</span></>)}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Stock Movement Export Modal */}
      {showStockMovementModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className={`rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            {/* Header */}
            <div className={`px-6 py-4 border-b flex items-center justify-between flex-shrink-0 ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-green-50'}`}>
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <FaCalendarAlt className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Export Stock Movement</h3>
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Select date range, location and optional filters</p>
                </div>
              </div>
              <button onClick={() => setShowStockMovementModal(false)} className={`p-2 rounded-full ${darkMode ? 'text-gray-400 hover:bg-gray-600' : 'text-gray-400 hover:bg-gray-100'}`}>
                <FaTimes className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-5 space-y-4 overflow-y-auto flex-grow">
              {/* Required Fields */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Start Date</label>
                  <input
                    type="date"
                    value={stockMovementFilters.startDate}
                    onChange={(e) => setStockMovementFilters(prev => ({ ...prev, startDate: e.target.value }))}
                    className={`w-full px-3 py-2 rounded-lg border focus:ring-2 focus:ring-green-500 focus:border-green-500 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>End Date</label>
                  <input
                    type="date"
                    value={stockMovementFilters.endDate}
                    onChange={(e) => setStockMovementFilters(prev => ({ ...prev, endDate: e.target.value }))}
                    className={`w-full px-3 py-2 rounded-lg border focus:ring-2 focus:ring-green-500 focus:border-green-500 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Location <span className="text-red-500">*</span></label>
                  <Select
                    placeholder="Select Location"
                    value={stockMovementFilters.location ? { value: stockMovementFilters.location, label: locations.find(l => l._id === stockMovementFilters.location)?.locationName || locations.find(l => l._id === stockMovementFilters.location)?.name } : null}
                    onChange={(option) => setStockMovementFilters(prev => ({ ...prev, location: option?.value || null }))}
                    options={locations.map(loc => ({ value: loc._id, label: loc.locationName || loc.name }))}
                    styles={darkSelectStyles}
                  />
                </div>
              </div>

              {/* Optional Filters */}
              <div>
                <p className={`text-sm font-medium mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Optional Filters</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Brand</label>
                    <Select
                      placeholder="All Brands"
                      isClearable
                      value={stockMovementFilters.brand ? { value: stockMovementFilters.brand, label: brands.find(b => b._id === stockMovementFilters.brand)?.name } : null}
                      onChange={(option) => setStockMovementFilters(prev => ({ ...prev, brand: option?.value || null }))}
                      options={brands.map(b => ({ value: b._id, label: b.name }))}
                      styles={darkSelectStyles}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Category</label>
                    <Select
                      placeholder="All Categories"
                      isClearable
                      value={stockMovementFilters.category ? { value: stockMovementFilters.category, label: categories.find(c => c._id === stockMovementFilters.category)?.name } : null}
                      onChange={(option) => setStockMovementFilters(prev => ({ ...prev, category: option?.value || null }))}
                      options={categories.map(c => ({ value: c._id, label: c.name }))}
                      styles={darkSelectStyles}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className={`border-t px-6 py-4 flex justify-end space-x-3 flex-shrink-0 ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
              <button
                onClick={() => setShowStockMovementModal(false)}
                className={`px-4 py-2 rounded-lg ${darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'}`}
              >
                Cancel
              </button>
              <button
                onClick={handleStockMovementExport}
                disabled={stockMovementLoading || !stockMovementFilters.startDate || !stockMovementFilters.endDate || !stockMovementFilters.location}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {stockMovementLoading ? (
                  <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div><span>Exporting...</span></>
                ) : (
                  <><FaFileExcel className="w-4 h-4" /><span>Export Excel</span></>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockItemsManagement;
